import { useCallback, useEffect, useState } from 'react';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';

import { bffQuery } from '../lib/bffClient';
import { useTranslation } from '../i18n';

/**
 * Clinic subscription & entitlement data.
 *
 * `capabilities` is the UI-gating source: it drives disabled buttons and the
 * paywall banner. It is NEVER the enforcement boundary — the BFF re-checks every
 * gated operation server-side, so a tampered client gains nothing.
 *
 * Deliberately tolerant of failure. If the subscription service is unreachable
 * the hook reports an entitled clinic rather than an error, mirroring the BFF's
 * fail-open policy: a billing outage must not blank the clinic panel or make the
 * app look broken to a clinic that has paid.
 */

const CAPABILITIES_QUERY = `
  query MyClinicCapabilities {
    myClinicCapabilities {
      entitled
      status
      currentPeriodEnd
      trialEndsAt
      graceEndsAt
      daysRemaining
      inTrial
      inGracePeriod
      blockedOperations
      paywallMessageFa
      readOnly
      trialEnded
      capabilitySet
      tierNameFa
      planCode
      limits { maxPatients maxDoctors maxStaff }
      billing { checkout }
    }
  }
`;

const SUBSCRIPTION_QUERY = `
  query MyClinicSubscription {
    myClinicSubscription {
      id
      status
      planCode
      currentPeriodStart
      currentPeriodEnd
      trialEndsAt
      graceEndsAt
      origin
      notRenewing
      notRenewingReason
      periods {
        id
        planCode
        periodStart
        periodEnd
        priceRial
        currency
        source
        activatedAt
        note
      }
    }
  }
`;

const PLANS_QUERY = `
  query SubscriptionPlans {
    subscriptionPlans {
      code
      nameFa
      priceRial
      priceToman
      compareAtPriceToman
      currency
      priceMinor
      compareAtPriceMinor
      promotionCode
      periodMonths
      capabilitySet
      sortOrder
    }
  }
`;

const CHECKOUT_MUTATION = `
  mutation StartSubscriptionCheckout($planCode: String!, $returnUrl: String) {
    startSubscriptionCheckout(planCode: $planCode, returnUrl: $returnUrl) {
      paymentId
      gateway
      authority
      paymentUrl
      amountRial
      amountToman
      currency
      amountMinor
    }
  }
`;

const CONFIRM_MUTATION = `
  mutation ConfirmSubscriptionPayment($authority: String!, $gateway: String) {
    confirmSubscriptionPayment(authority: $authority, gateway: $gateway) {
      success
      status
      refId
      messageFa
      subscription { id status currentPeriodEnd planCode }
    }
  }
`;

const NOT_RENEWING_MUTATION = `
  mutation SetNotRenewing($notRenewing: Boolean!, $reason: String) {
    setNotRenewing(notRenewing: $notRenewing, reason: $reason) { id notRenewing notRenewingReason }
  }
`;

/**
 * Treated as entitled AND as the top tier — matches the BFF's fail-open
 * contract on both axes. Reporting a lower tier, or a read-only panel, here
 * would grey out every screen during a billing outage, which is a worse lie
 * than reporting nothing.
 */
const OPEN_CAPABILITIES = {
  entitled: true,
  status: 'ACTIVE',
  currentPeriodEnd: null,
  trialEndsAt: null,
  graceEndsAt: null,
  daysRemaining: null,
  inTrial: false,
  inGracePeriod: false,
  blockedOperations: [],
  paywallMessageFa: null,
  readOnly: false,
  trialEnded: false,
  capabilitySet: 'CLINIC_GOLD',
  tierNameFa: '',
  planCode: null,
  limits: { maxPatients: null, maxDoctors: null, maxStaff: null },
  // Unknown while the BFF is unreachable: today's behaviour. The BFF still
  // refuses checkout itself where there is no way to pay (CHECKOUT_UNAVAILABLE).
  billing: { checkout: 'ON' },
  unavailable: true,
};

/**
 * Capabilities only — cheap enough for the layout to hold on every page.
 * Used by the paywall banner and by any screen disabling a gated action.
 */
export function useClinicCapabilities() {
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(() => {
    setLoading(true);
    return bffQuery(CAPABILITIES_QUERY)
      .then((data) => setCapabilities(data?.myClinicCapabilities || OPEN_CAPABILITIES))
      .catch(() => setCapabilities(OPEN_CAPABILITIES))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  // A clinic that pays in another tab should not have to reload to see the
  // paywall disappear.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  return { capabilities, loading, refetch };
}

/**
 * Whether one operation is currently blocked. UI affordance only.
 *
 * Deliberately does NOT short-circuit on `entitled`: an entitled clinic still
 * mid-term on a retired lower tier cannot open every طلایی screen, and
 * `blockedOperations` is already the union of both gates; trust it.
 */
export function isBlocked(capabilities, operation) {
  if (!capabilities) return false;
  return (capabilities.blockedOperations || []).includes(operation);
}

/** Full billing screen data: subscription, plans, and the mutations. */
/**
 * The plans as this market shows them. A promotion is an Iranian campaign (the
 * congress offer, CONGRESS_RESPIRATORY_1405): a brand outside the Iranian market
 * (`market: 'intl'`, docs/rebranding/CONTRACT.md) names none, so a code that
 * reaches it anyway is dropped here — no banner, no «تخفیف ویژه» tag.
 */
function offeredPlans(plans) {
  const list = Array.isArray(plans) ? plans : [];
  return isIranMarket() ? list : list.map((plan) => ({ ...plan, promotionCode: null }));
}

export function useSubscription() {
  const { t } = useTranslation();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Plans and subscription are fetched together but fail independently: a
      // clinic with no subscription row yet must still see what it can buy.
      const [subData, planData] = await Promise.all([
        bffQuery(SUBSCRIPTION_QUERY).catch(() => null),
        bffQuery(PLANS_QUERY),
      ]);
      setSubscription(subData?.myClinicSubscription || null);
      setPlans(offeredPlans(planData?.subscriptionPlans));
    } catch (err) {
      setError(err?.message || t('errors.load.subscription'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { refetch(); }, [refetch]);

  /**
   * Start checkout and hand back the gateway URL.
   *
   * Returns the session rather than redirecting, so the caller decides when to
   * leave the page — a redirect buried in a hook is impossible to test and
   * surprises the user mid-render.
   */
  const startCheckout = useCallback(async (planCode) => {
    setBusy(planCode);
    try {
      // Where the BROWSER comes back to. Where the GATEWAY posts its result is
      // decided by the server: it must be an endpoint that can parse a bank's
      // POST and settle, and this app is static files — sending Shaparak here
      // returned 405, so the payment never settled and the bank reversed it.
      // Where the BROWSER comes back to — «کلینیک من», billing view. The
      // payment BFF appends `payment=…` to this with `&` when a query is already
      // present, so the `?view=` survives the round trip.
      const returnUrl = `${window.location.origin}/clinic?view=billing`;
      const data = await bffQuery(CHECKOUT_MUTATION, { planCode, returnUrl });
      return data?.startSubscriptionCheckout || null;
    } finally {
      setBusy('');
    }
  }, []);

  const confirmPayment = useCallback(async (authority, gateway) => {
    const data = await bffQuery(CONFIRM_MUTATION, { authority, gateway: gateway || 'zarinpal' });
    await refetch();
    return data?.confirmSubscriptionPayment || null;
  }, [refetch]);

  const setNotRenewing = useCallback(async (notRenewing, reason) => {
    const data = await bffQuery(NOT_RENEWING_MUTATION, { notRenewing, reason: reason || '' });
    await refetch();
    return data?.setNotRenewing || null;
  }, [refetch]);

  return {
    subscription, plans, loading, error, busy,
    refetch, startCheckout, confirmPayment, setNotRenewing,
  };
}
